import { LitElement, unsafeCSS, type CSSResultGroup } from "lit";

// `?inline` demande à Vite la CSS **compilée par Tailwind**, sous forme de chaîne, au lieu
// de l'injecter dans le document. C'est ce qui permet de la faire entrer dans un shadow root.
import tailwindCss from "../styles/tailwind.css?inline";

/**
 * Classe de base de tous les composants du starter.
 * ================================================
 *
 * ── Le problème qu'elle résout ──────────────────────────────────────────────
 * Tailwind produit des classes **globales**. Lit rend dans un **Shadow DOM**, une frontière
 * que le CSS global ne franchit pas. Sans cette classe, `class="flex gap-4"` dans un
 * composant Lit ne produit **rien** — et sans erreur, ce qui en fait un piège coûteux.
 *
 * ── Pourquoi ne pas simplement supprimer le Shadow DOM ? ────────────────────
 * C'est l'autre solution courante (`createRenderRoot() { return this; }`), et elle marche.
 * Mais elle jette l'encapsulation, c'est-à-dire la raison d'être des web components : les
 * styles d'un écran pourraient alors fuir sur un autre. Un starter doit enseigner le bon
 * réflexe, pas le raccourci.
 *
 * ── Le coût réel ────────────────────────────────────────────────────────────
 * Aucun, ou presque : Lit transforme cette CSS en `CSSStyleSheet` constructible **une
 * seule fois**, partagée par référence entre toutes les instances. Ce n'est pas une copie
 * de Tailwind par composant.
 *
 * ── Usage ───────────────────────────────────────────────────────────────────
 *   class MyScreen extends TailwindElement { … }                    // rien de plus
 *
 *   // Avec des styles spécifiques, garder `TailwindElement.styles` en premier :
 *   static override styles = [
 *     ...TailwindElement.baseStyles,
 *     css`:host { display: block; }`,
 *   ];
 */
export class TailwindElement extends LitElement {
  /** À réutiliser quand un composant ajoute ses propres styles. */
  static readonly baseStyles: CSSResultGroup[] = [unsafeCSS(tailwindCss)];

  static override styles: CSSResultGroup = TailwindElement.baseStyles;
}
